import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Package, 
  DollarSign, 
  Hash, 
  Tag, 
  Warehouse, 
  Calendar,
  TrendingUp,
  TrendingDown,
  Camera,
  Upload,
  Download,
  QrCode,
  Printer,
  Edit,
  Trash2
} from 'lucide-react';
import BarcodeGenerator from '../components/BarcodeGenerator';
import ImageUploader from '../components/ImageUploader';
import MultiSKUManager from '../components/MultiSKUManager';
import { productsService } from '../services/productsService';
import { entriesService } from '../services/entriesService';
import { exitsService } from '../services/exitsService';
import { imagesService } from '../services/imagesService';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [entries, setEntries] = useState([]);
  const [exits, setExits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Cargar datos del producto
  useEffect(() => {
    const loadProductData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Cargando detalles del producto ID:', id);

        // Cargar producto
        const productData = await productsService.getById(id);
        console.log('📦 Producto cargado:', productData);
        console.log('📦 JSON del producto:', JSON.stringify(productData, null, 2));
        console.log('📦 Propiedades del producto:', Object.keys(productData));
        setProduct(productData);
        
        // Inicializar formulario de edición
        setEditForm({
          name: productData?.name || '',
          sku: productData?.sku || productData?.code || '',
          category: productData?.category || '',
          subcategory: productData?.subcategory || '',
          brand: productData?.brand || '',
          model: productData?.model || '',
          price: productData?.unit_price || productData?.price || '', // CORREGIDO
          cost_price: productData?.cost_price || '',
          stock: productData?.current_stock || productData?.stock || '', // CORREGIDO
          unit: productData?.unit || 'piezas', // CORREGIDO
          min_stock: productData?.min_stock || '',
          max_stock: productData?.max_stock || '',
          location: productData?.location || '',
          description: productData?.description || '',
          barcode: productData?.barcode || '',
          weight: productData?.weight || '',
          dimensions: productData?.dimensions || '',
          supplier_id: productData?.supplier_id || '',
          is_active: productData?.is_active !== undefined ? productData.is_active : true
        });

        // Cargar entradas del producto
        try {
          console.log('🔄 Intentando cargar entradas para el producto ID:', id);
          const entriesData = await entriesService.getByProductId(id);
          console.log('✅ Datos de entradas recibidos:', entriesData);
          console.log('✅ JSON de entradas:', JSON.stringify(entriesData, null, 2));
          
          if (!entriesData) {
            console.warn('⚠️ No se recibieron datos de entradas (undefined o null)');
            setEntries([]);
          } else if (Array.isArray(entriesData)) {
            console.log('✅ Entradas como array:', entriesData.length, 'elementos');
            setEntries(entriesData);
          } else if (entriesData.entries && Array.isArray(entriesData.entries)) {
            // En caso que el backend envíe { entries: [...] }
            console.log('✅ Entradas dentro de objeto:', entriesData.entries.length, 'elementos');
            setEntries(entriesData.entries);
          } else {
            console.warn('⚠️ Formato inesperado de datos:', typeof entriesData);
            setEntries([]);
          }
        } catch (entriesError) {
          console.error('❌ Error cargando entradas:', entriesError);
          console.error('❌ Detalles:', entriesError.message);
          if (entriesError.response) {
            console.error('❌ Respuesta del servidor:', entriesError.response.data);
            console.error('❌ Status:', entriesError.response.status);
          }
          setEntries([]);
          setError(error => error ? `${error}. Error cargando entradas.` : 'Error cargando entradas.');
        }

        // Cargar salidas del producto
        try {
          console.log('🔄 Intentando cargar salidas para el producto ID:', id);
          const exitsData = await exitsService.getByProductId(id);
          console.log('✅ Datos de salidas recibidos:', exitsData);
          
          if (!exitsData) {
            console.warn('⚠️ No se recibieron datos de salidas (undefined o null)');
            setExits([]);
          } else if (Array.isArray(exitsData)) {
            console.log('✅ Salidas como array:', exitsData.length, 'elementos');
            setExits(exitsData);
          } else if (exitsData.exits && Array.isArray(exitsData.exits)) {
            // En caso que el backend envíe { exits: [...] }
            console.log('✅ Salidas dentro de objeto:', exitsData.exits.length, 'elementos');
            setExits(exitsData.exits);
          } else {
            console.warn('⚠️ Formato inesperado de datos:', typeof exitsData);
            setExits([]);
          }
        } catch (exitsError) {
          console.error('❌ Error cargando salidas:', exitsError);
          console.error('❌ Detalles:', exitsError.message);
          if (exitsError.response) {
            console.error('❌ Respuesta del servidor:', exitsError.response.data);
            console.error('❌ Status:', exitsError.response.status);
          }
          setExits([]);
          setError(error => error ? `${error}. Error cargando salidas.` : 'Error cargando salidas.');
        }

      } catch (error) {
        console.error('❌ Error cargando datos del producto:', error);
        setError('Error al cargar los detalles del producto');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadProductData();
    }
  }, [id]);

  const handleImageUpload = async (imageFileName) => {
    try {
      setIsUploading(true);
      
      // Eliminar imagen anterior si existe
      if (product.image_url) {
        try {
          await imagesService.deleteImage(product.image_url);
        } catch (error) {
          console.warn('No se pudo eliminar la imagen anterior:', error);
        }
      }
      
      // Actualizar el producto con la nueva imagen
      const updatedProduct = { ...product, image_url: imageFileName };
      
      // Guardar en el backend
      await productsService.update(product.id, updatedProduct);
      
      // Actualizar el estado local
      setProduct(updatedProduct);
      setShowImageUploader(false);
      
      console.log('✅ Imagen del producto actualizada exitosamente');
    } catch (error) {
      console.error('❌ Error actualizando imagen del producto:', error);
      alert('Error al actualizar la imagen del producto');
    } finally {
      setIsUploading(false);
    }
  };

  const getTotalEntries = () => {
    if (!entries || entries.length === 0) return 0;
    
    // Depuración detallada
    console.log('📊 Calculando total de entradas...');
    console.log('📦 Datos de entradas:', entries);
    
    let total = 0;
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const rawQuantity = entry.quantity;
      const parsedQuantity = parseFloat(rawQuantity) || 0;
      
      console.log(`Entrada ${i+1}: Valor original='${rawQuantity}', Tipo=${typeof rawQuantity}, Parseado=${parsedQuantity}`);
      
      total += parsedQuantity;
    }
    
    console.log('💯 Total calculado:', total);
    return total;
  };

  const getTotalExits = () => {
    if (!exits || exits.length === 0) return 0;
    
    // Depuración detallada
    console.log('📊 Calculando total de salidas...');
    console.log('📦 Datos de salidas:', exits);
    
    let total = 0;
    for (let i = 0; i < exits.length; i++) {
      const exit = exits[i];
      const rawQuantity = exit.quantity;
      const parsedQuantity = parseFloat(rawQuantity) || 0;
      
      console.log(`Salida ${i+1}: Valor original='${rawQuantity}', Tipo=${typeof rawQuantity}, Parseado=${parsedQuantity}`);
      
      total += parsedQuantity;
    }
    
    console.log('💯 Total calculado (salidas):', total);
    return total;
  };

  const getMovementBalance = () => {
    const totalEntries = getTotalEntries();
    const totalExits = getTotalExits();
    return totalEntries - totalExits;
  };

  const getStockValue = () => {
    const stockQuantity = parseInt(product?.current_stock || product?.stock || 0);
    // Usar el costo promedio si existe, de lo contrario usar los otros precios
    const unitCost = parseFloat(
      product?.average_cost || 
      product?.cost_price || 
      product?.unit_price || 
      product?.price || 
      0
    );
    
    console.log('📊 Cálculo de valor de stock:', {
      producto: product?.name,
      sku: product?.sku,
      cantidad: stockQuantity,
      costoUnitario: unitCost,
      valorTotal: stockQuantity * unitCost
    });
    
    return stockQuantity * unitCost;
  };

  const formatMoney = (amount) => {
    const formatter = new Intl.NumberFormat('es-MX', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    });
    return `$${formatter.format(amount)} MXN`;
  };

  const downloadBarcode = () => {
    try {
      const canvas = document.querySelector('#barcode-canvas canvas');
      if (canvas) {
        const link = document.createElement('a');
        link.download = `barcode-${product.sku || product.code || product.id}.png`;
        link.href = canvas.toDataURL();
        link.click();
      }
    } catch (error) {
      console.error('Error descargando código de barras:', error);
      alert('Error al descargar el código de barras');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Restaurar valores originales
    setEditForm({
      name: product.name || '',
      sku: product.sku || product.code || '',
      category: product.category || '',
      subcategory: product.subcategory || '',
      brand: product.brand || '',
      model: product.model || '',
      price: product.unit_price || product.price || '', // CORREGIDO
      cost_price: product.cost_price || '',
      stock: product.current_stock || product.stock || '', // CORREGIDO
      unit: product.unit || 'piezas', // CORREGIDO
      min_stock: product.min_stock || '',
      max_stock: product.max_stock || '',
      location: product.location || '',
      description: product.description || '',
      barcode: product.barcode || '',
      weight: product.weight || '',
      dimensions: product.dimensions || '',
      supplier_id: product.supplier_id || '',
      is_active: product.is_active !== undefined ? product.is_active : true
    });
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      
      // Mapear los campos correctamente para el backend
      const productData = {
        name: editForm.name || '',
        code: editForm.sku || editForm.code || '',
        sku: editForm.sku || editForm.code || '',
        category: editForm.category || '',
        subcategory: editForm.subcategory || '',
        brand: editForm.brand || '',
        model: editForm.model || '',
        current_stock: parseInt(editForm.stock) || 0,  // ← CORREGIDO: usar current_stock
        unit: editForm.unit || 'unidad',
        unit_price: parseFloat(editForm.price) || 0,
        cost_price: parseFloat(editForm.cost_price) || 0,
        location: editForm.location || '',
        min_stock: parseInt(editForm.min_stock) || 0,
        max_stock: parseInt(editForm.max_stock) || 0,
        description: editForm.description || '',
        image: product.image_url || '',
        barcode: editForm.barcode || '',
        weight: editForm.weight || '',
        dimensions: editForm.dimensions || '',
        supplier_id: editForm.supplier_id || null,
        is_active: editForm.is_active !== undefined ? editForm.is_active : true
      };
      
      console.log('🔄 Enviando datos de producto:', productData);
      
      const result = await productsService.update(id, productData);
      console.log('✅ Resultado actualización:', result);
      
      // Extraer el producto actualizado de la respuesta
      const updatedProduct = result.success && result.data ? result.data : result;
      
      setProduct(updatedProduct);
      setIsEditing(false);
      console.log('✅ Producto actualizado:', updatedProduct);
    } catch (error) {
      console.error('❌ Error actualizando producto:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDelete = async () => {
    try {
      await productsService.delete(id);
      // Después de eliminar, intentar volver al listado preservando página
      try {
        if (window.history.length > 1) {
          navigate(-1);
          return;
        }
      } catch (e) {}
      const params = new URLSearchParams(location.search || window.location.search);
      const page = params.get('page') || sessionStorage.getItem('inventory_page') || '1';
      navigate(`/inventory?page=${page}`);
    } catch (error) {
      console.error('❌ Error eliminando producto:', error);
      setError(error.message);
    }
  };

  const location = useLocation();

  const handleGoBack = () => {
    // Navegar directamente al inventario preservando ?page (determinístico)
    const params = new URLSearchParams(location.search || window.location.search);
    const page = params.get('page') || sessionStorage.getItem('inventory_page') || '1';
    navigate(`/inventory?page=${page}`);
  };

  const printBarcode = () => {
    try {
      const canvas = document.querySelector('#barcode-canvas canvas');
      if (canvas) {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <html>
            <head>
              <title>Código de Barras - ${product.name}</title>
              <style>
                body { 
                  margin: 0; 
                  padding: 20px; 
                  font-family: Arial, sans-serif; 
                  text-align: center; 
                }
                .barcode-container { 
                  margin: 20px auto; 
                  display: inline-block; 
                }
                .sku-info { 
                  margin-top: 10px;
                  font-size: 14px;
                  font-weight: bold;
                }
              </style>
            </head>
            <body>
              <div class="barcode-container">
                <img src="${canvas.toDataURL()}" alt="Código de barras" />
                <div class="sku-info">SKU: ${product.sku || product.code || 'N/A'}</div>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    } catch (error) {
      console.error('Error imprimiendo código de barras:', error);
      alert('Error al imprimir el código de barras');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-blue-700 font-medium">Cargando ficha técnica...</p>
        </div>
      </div>
    );
  }

  if (error || !product || !product.id) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-700 mb-4">{error || 'Producto no encontrado o datos incompletos'}</p>
          <button 
            onClick={() => {
              try { if (window.history.length > 1) { navigate(-1); return; } } catch(e){}
              const params = new URLSearchParams(window.location.search);
              const page = params.get('page') || sessionStorage.getItem('inventory_page') || '1';
              navigate(`/inventory?page=${page}`);
            }}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Volver al Inventario
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header con botón de regreso y acciones */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al Inventario
          </button>
          
          <div className="flex items-center gap-3">
            {isEditing ? (
              <>
                <motion.button
                  onClick={handleSaveEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Download className="w-4 h-4" />
                  Guardar
                </motion.button>
                <motion.button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancelar
                </motion.button>
              </>
            ) : (
              <>
                <motion.button
                  onClick={handleEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </motion.button>
                <motion.button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </motion.button>
              </>
            )}
          </div>
        </div>

        {/* Título principal */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-extrabold text-gray-800 mb-2 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Ficha Técnica del Producto
          </h1>
          <p className="text-gray-600">Información detallada y movimientos de inventario</p>
        </motion.div>

        {/* Contenido principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Columna izquierda - Información básica e imagen */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1 space-y-6"
          >
            {/* Imagen del producto */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Camera className="w-5 h-5 text-blue-600" />
                Imagen del Producto
              </h3>
              
              <div className="relative bg-gray-50 flex items-center justify-center">
                {product.image_url ? (
                  <img
                    src={imagesService.getImageUrl(product.image_url)}
                    alt={product.name}
                    className="max-w-full max-h-64 object-contain rounded-lg border border-gray-200"
                    onError={(e) => {
                      console.error('Error cargando imagen del producto');
                      e.target.src = '';
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                
                {!product.image_url && (
                  <div className="w-full h-64 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-2" />
                      <p>Sin imagen</p>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={() => setShowImageUploader(true)}
                  className="absolute top-2 right-2 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors duration-300 shadow-lg"
                  title="Subir/cambiar imagen"
                >
                  <Upload className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Código de barras */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <QrCode className="w-5 h-5 text-blue-600" />
                Código de Barras
              </h3>
              
              <div id="barcode-canvas" className="bg-gray-50 p-4 rounded-lg">
                <BarcodeGenerator 
                  value={product.sku || product.code || (product.id ? product.id.toString() : 'NO-CODE')} 
                  productName={product.name}
                />
              </div>
              
              <div className="mt-3 text-center">
                <p className="text-sm text-gray-600 mb-3">SKU: {product.sku || 'No disponible'}</p>
                
                {/* Botones de descarga e impresión */}
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={downloadBarcode}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-300 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Descargar
                  </button>
                  
                  <button
                    onClick={printBarcode}
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300 text-sm"
                  >
                    <Printer className="w-4 h-4" />
                    Imprimir
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Columna derecha - Información detallada */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Información básica del producto */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Package className="w-6 h-6 text-blue-600" />
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="text-2xl font-bold text-gray-800 bg-gray-50 border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  product.name
                )}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Hash className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Código</p>
                      <p className="font-semibold">{product.code || 'No disponible'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Tag className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">SKU</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.sku}
                          onChange={(e) => handleInputChange('sku', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold">{product.sku || 'No disponible'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Categoría</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.category}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold">{product.category || 'Sin categoría'}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Subcategoría</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.subcategory}
                          onChange={(e) => handleInputChange('subcategory', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold">{product.subcategory || 'Sin subcategoría'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Marca</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.brand}
                          onChange={(e) => handleInputChange('brand', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold">{product.brand || 'Sin marca'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Modelo</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.model}
                          onChange={(e) => handleInputChange('model', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold">{product.model || 'Sin modelo'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Peso</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.weight}
                          onChange={(e) => handleInputChange('weight', e.target.value)}
                          placeholder="ej: 1.5 kg"
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold">{product.weight || 'Sin peso'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Dimensiones</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.dimensions}
                          onChange={(e) => handleInputChange('dimensions', e.target.value)}
                          placeholder="ej: 10x20x30 cm"
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold">{product.dimensions || 'Sin dimensiones'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Hash className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Código de Barras</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.barcode}
                          onChange={(e) => handleInputChange('barcode', e.target.value)}
                          placeholder="Código de barras"
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold">{product.barcode || 'Sin código de barras'}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Warehouse className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Stock Actual</p>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editForm.stock}
                          onChange={(e) => handleInputChange('stock', e.target.value)}
                          className="w-full font-semibold text-2xl text-blue-600 bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold text-2xl text-blue-600">{product.current_stock || product.stock || product.quantity || 0}</p>
                      )}
                    </div>
                  </div>

                  {/* Indicador visual de stock */}
                  <div className="bg-gray-50 p-4 rounded-lg border">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Control de Stock</h4>
                    <div className="space-y-3">
                      {/* Barra de progreso de stock */}
                      <div className="relative">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Mínimo: {product.min_stock || 0}</span>
                          <span>Actual: {product.current_stock || 0}</span>
                          <span>Máximo: {product.max_stock || 0}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              (() => {
                                const currentStock = product.current_stock || 0;
                                const minStock = product.min_stock || 0;
                                const maxStock = product.max_stock || 100;
                                
                                if (currentStock === 0) {
                                  return 'bg-red-500';
                                } else if (currentStock <= minStock) {
                                  return 'bg-red-500';
                                } else if (currentStock < maxStock * 0.7) {
                                  return 'bg-yellow-500';
                                } else {
                                  return 'bg-green-500';
                                }
                              })()
                            }`}
                            style={{
                              width: `${Math.min(100, Math.max(0, 
                                ((product.current_stock || 0) / Math.max(product.max_stock || 100, 1)) * 100
                              ))}%`
                            }}
                          ></div>
                        </div>
                        {/* Marcadores de mínimo y máximo */}
                        <div className="relative mt-1">
                          <div 
                            className="absolute w-0.5 h-3 bg-red-400"
                            style={{
                              left: `${Math.min(100, Math.max(0, 
                                ((product.min_stock || 0) / Math.max(product.max_stock || 100, 1)) * 100
                              ))}%`
                            }}
                            title={`Mínimo: ${product.min_stock || 0}`}
                          ></div>
                          <div 
                            className="absolute w-0.5 h-3 bg-yellow-400"
                            style={{ left: '100%' }}
                            title={`Máximo: ${product.max_stock || 0}`}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Edición de stock mínimo y máximo */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Stock Mínimo</label>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editForm.min_stock}
                              onChange={(e) => handleInputChange('min_stock', e.target.value)}
                              className="w-full text-sm bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-sm font-medium text-red-600">{product.min_stock || '0'}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Stock Máximo</label>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editForm.max_stock}
                              onChange={(e) => handleInputChange('max_stock', e.target.value)}
                              className="w-full text-sm bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-sm font-medium text-yellow-600">{product.max_stock || '0'}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Estado del stock */}
                      <div className="text-center">
                        {(() => {
                          const currentStock = product.current_stock || 0;
                          const minStock = product.min_stock || 0;
                          const maxStock = product.max_stock || 100;
                          
                          let stockStatus = {
                            bgClass: 'bg-green-100',
                            textClass: 'text-green-800',
                            icon: '✅',
                            label: 'Stock Normal'
                          };
                          
                          if (currentStock === 0) {
                            stockStatus = {
                              bgClass: 'bg-red-100',
                              textClass: 'text-red-800',
                              icon: '🔴',
                              label: 'AGOTADO'
                            };
                          } else if (currentStock <= minStock) {
                            stockStatus = {
                              bgClass: 'bg-red-100',
                              textClass: 'text-red-800',
                              icon: '🟠',
                              label: 'CRÍTICO'
                            };
                          } else if (currentStock < maxStock * 0.7) {
                            stockStatus = {
                              bgClass: 'bg-yellow-100',
                              textClass: 'text-yellow-800',
                              icon: '🟡',
                              label: 'BAJO'
                            };
                          }
                          
                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.bgClass} ${stockStatus.textClass}`}>
                              {stockStatus.icon} {stockStatus.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Precio</p>
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.price}
                          onChange={(e) => handleInputChange('price', e.target.value)}
                          className="w-full font-semibold text-xl text-green-600 bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold text-xl text-green-600">
                          ${product.unit_price ? parseFloat(product.unit_price).toFixed(2) : '0.00'}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Costo</p>
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.cost_price}
                          onChange={(e) => handleInputChange('cost_price', e.target.value)}
                          className="w-full font-semibold text-lg text-orange-600 bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold text-lg text-orange-600">
                          ${product.cost_price ? parseFloat(product.cost_price).toFixed(2) : '0.00'}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Costo Promedio */}
                  {product.average_cost && (
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Costo Promedio</p>
                        <p className="font-semibold text-lg text-blue-600">
                          ${parseFloat(product.average_cost).toFixed(2)}
                        </p>
                        {product.last_average_update && (
                          <p className="text-xs text-gray-500">
                            Actualizado: {new Date(product.last_average_update).toLocaleDateString()} 
                            {' '}{new Date(product.last_average_update).toLocaleTimeString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Unidad</p>
                      {isEditing ? (
                        <select
                          value={editForm.unit}
                          onChange={(e) => handleInputChange('unit', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="piezas">Piezas</option>
                          <option value="metros">Metros</option>
                          <option value="litros">Litros</option>
                          <option value="kilos">Kilos</option>
                          <option value="unidad">Unidad</option>
                          <option value="lote">Lote</option>
                          <option value="caja">Caja</option>
                          <option value="paquete">Paquete</option>
                        </select>
                      ) : (
                        <p className="font-semibold">{product.unit || 'piezas'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Warehouse className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Ubicación</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.location}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="font-semibold">{product.location || 'No especificada'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Fecha de Creación</p>
                      <p className="font-semibold">
                        {product.created_at 
                          ? new Date(product.created_at).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'No disponible'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Descripción */}
              <div className="mt-6">
                <h4 className="font-semibold text-gray-800 mb-2">Descripción</h4>
                {isEditing ? (
                  <textarea
                    value={editForm.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full text-gray-600 bg-gray-50 border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Descripción del producto..."
                  />
                ) : (
                  <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">
                    {product.description || 'Sin descripción'}
                  </p>
                )}
              </div>
            </div>

            {/* Estadísticas de movimientos */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Resumen de Movimientos</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-green-600 font-medium">Total Entradas</p>
                  <p className="text-2xl font-bold text-green-700">
                    {entries ? entries.length : 0} movimiento(s)
                  </p>
                  <p className="text-xs text-green-500">Cantidad: {entries && entries.length > 0 
                      ? getTotalEntries().toFixed(2) 
                      : "0.00"}</p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <TrendingDown className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <p className="text-sm text-red-600 font-medium">Total Salidas</p>
                  <p className="text-2xl font-bold text-red-700">
                    {exits ? exits.length : 0} movimiento(s)
                  </p>
                  <p className="text-xs text-red-500">Cantidad: {exits && exits.length > 0 
                      ? getTotalExits().toFixed(2) 
                      : "0.00"}</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <DollarSign className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-blue-600 font-medium">Valor Total del Stock</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {parseInt(product.current_stock || product.stock || 0)} piezas
                  </p>
                  <p className="text-xs text-blue-500">
                    Valor: {formatMoney(getStockValue())}
                  </p>
                </div>
              </div>
            </div>

            {/* Gestión de SKUs múltiples */}
            <MultiSKUManager 
              productId={product.id} 
              onSkusUpdate={() => {
                // Recargar datos del producto si es necesario
                console.log('SKUs actualizados');
              }}
            />

            {/* Historial de movimientos */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Historial de Movimientos</h3>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {/* Entradas */}
                {entries.map((entry, index) => (
                  <motion.div
                    key={`entry-${entry.id}-${index}-${entry.created_at || Date.now()}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Entrada</p>
                        <p className="text-sm text-green-600">
                          {entry.entry_date ? (() => {
                            const date = new Date(entry.entry_date);
                            const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                            const localDate = new Date(date.getTime() + userTimezoneOffset);
                            return localDate.toLocaleDateString();
                          })() : 'Sin fecha'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-700">+{entry.quantity}</p>
                      <p className="text-sm text-green-600">
                        ${entry.unit_cost ? parseFloat(entry.unit_cost).toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </motion.div>
                ))}

                {/* Salidas */}
                {exits.map((exit, index) => (
                  <motion.div
                    key={`exit-${exit.id}-${index}-${exit.created_at || Date.now()}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="font-medium text-red-800">Salida</p>
                        <p className="text-sm text-red-600">
                          {exit.exit_date ? (() => {
                            const date = new Date(exit.exit_date);
                            const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                            const localDate = new Date(date.getTime() + userTimezoneOffset);
                            return localDate.toLocaleDateString();
                          })() : 'Sin fecha'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-700">-{exit.quantity}</p>
                      <p className="text-sm text-red-600">
                        ${exit.unit_cost ? parseFloat(exit.unit_cost).toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </motion.div>
                ))}

                {entries.length === 0 && exits.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No hay movimientos registrados para este producto</p>
                    <p className="text-xs mt-2 text-gray-400">
                      (Si crees que debería haber movimientos, intenta recargar la página
                      o verifica la conexión con el servidor)
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Historial de costos promedios */}
            {product.average_cost && (
              <div className="bg-white rounded-3xl shadow-md p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-6">Historial de Costos Promedios</h3>
                <div className="overflow-hidden rounded-xl border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo Anterior</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nuevo Costo</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cambio</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entrada</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* En el futuro, aquí se mostrarán los registros del historial de costos */}
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                          <DollarSign className="w-8 h-8 mx-auto mb-3 text-gray-300" />
                          <p>El historial de costos estará disponible en una próxima actualización</p>
                          <p className="text-xs mt-2 text-gray-400">
                            (Los nuevos registros comenzarán a aparecer automáticamente)
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Modal para subir imagen */}
      <AnimatePresence>
        {showImageUploader && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">Subir Imagen del Producto</h3>
              
              <ImageUploader
                onImageUpload={handleImageUpload}
                currentImage={product.image_url}
                onCancel={() => setShowImageUploader(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de Confirmación de Eliminación */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Confirmar Eliminación
              </h3>
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que quieres eliminar este producto? Esta acción no se puede deshacer y afectará todos los registros relacionados.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductDetailPage;
